import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Bed, Bath, Car, Maximize, Camera, MapPin, BadgeCheck, Sparkles, ArrowUpRight, Building2, FileText } from 'lucide-react';
import { formatCOP } from '../mock';
import { thumbUrl, api } from '../lib/api';
import { isDual, getPrices, operationLabel } from '../lib/operations';
import { useAuth } from '../context/AuthContext';
import ShareButton from './ShareButton';
import { track, EventTypes } from '../lib/analytics';
import advisor from '../lib/advisor';
import { buildPropertyWhatsAppLink, WhatsAppIcon } from '../lib/whatsapp';
import HabitaScoreBadge from './HabitaScoreBadge';

export default function PropertyCard({ p, layout = 'grid', advisorReason = null, habitaScore = null }) {
  const { user } = useAuth();
  const [fav, setFav] = useState(p.isFavorite || false);
  const [imgIdx, setImgIdx] = useState(0);
  const isNewProject = p.source === 'constructor_project';

  const handleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;
    try {
      if (fav) {
        await api.removeFavorite(p.id);
        track(EventTypes.PROPERTY_UNFAVORITED, { property_id: p.id, source: 'card' });
      } else {
        await api.addFavorite(p.id);
        track(EventTypes.PROPERTY_FAVORITED, { property_id: p.id, source: 'card' });
      }
      setFav(!fav);
    } catch (err) { void err; }
  };

  // Generate Cloudinary thumbs at 3 sizes for srcset (responsive)
  const imgSet = p.images && p.images.length
    ? p.images.map((u) => ({
        sm: thumbUrl(u, { w: 480, h: 360 }),
        md: thumbUrl(u, { w: 720, h: 540 }),
        lg: thumbUrl(u, { w: 960, h: 720 }),
      }))
    : [{ sm: '', md: '', lg: 'https://via.placeholder.com/640x480?text=Sin+foto' }];
  const imgs = imgSet.map(s => s.md);
  const current = imgSet[imgIdx] || imgSet[0];
  const isPlaceholder = !current.sm;

  const agent = p.agent;
  const showVerified = !!p.verified;
  const showAgentVerified = !!(agent && agent.verified);
  const opLabel = operationLabel(p);
  const dual = isDual(p);
  const dualPrices = getPrices(p);

  // Deep-link WhatsApp compartido (mensaje simple para las cards).
  const waHref = agent?.phone ? buildPropertyWhatsAppLink(agent.phone, p, { variant: 'card' }) : null;

  const handleWhatsAppClick = (e) => {
    // Dejar que el <a> abra en nueva pestaña; sólo interceptamos telemetría.
    e.stopPropagation();
    track(EventTypes.CONTACT_WHATSAPP, {
      property_id: p.id,
      agent_id: p.owner_id,
      source: 'card',
    });
    try {
      advisor.updateContext('contact_intent', {
        property_id: p.id,
        channel: 'whatsapp',
        source: 'card',
      });
    } catch { /* silent */ }
  };

  if (layout === 'list') {
    return (
      <div
        data-testid={`property-card-${p.id}`}
        className="flex flex-col md:flex-row bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md hover:border-habita/30 transition-all"
      >
        <Link
          to={`/propiedad/${p.id}`}
          className="relative md:w-[42%] aspect-[16/10] md:aspect-auto overflow-hidden bg-gray-100 shrink-0 group"
        >
          <img
            src={current.lg}
            srcSet={!isPlaceholder ? `${current.sm} 480w, ${current.md} 720w, ${current.lg} 960w` : undefined}
            sizes="(max-width: 768px) 100vw, 480px"
            alt={p.title}
            width="720"
            height="540"
            className="w-full h-full object-cover gallery-img transition-transform duration-700 group-hover:scale-[1.03]"
            loading="lazy"
            decoding="async"
          />
          <button
            data-testid={`fav-btn-${p.id}`}
            onClick={handleFav}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 hover:bg-white flex items-center justify-center transition-colors shadow-sm"
            aria-label="Favorito"
          >
            <Heart className={`w-4 h-4 ${fav ? 'fill-rose-500 text-rose-500' : 'text-gray-700'}`} />
          </button>
          {habitaScore && (
            <div className="absolute bottom-3 right-3" data-testid={`habita-score-${p.id}`}>
              <HabitaScoreBadge score={habitaScore} size="sm" showLabel={false} />
            </div>
          )}
          <div className="absolute bottom-3 left-3 px-2.5 py-1 bg-black/55 backdrop-blur rounded-md text-white text-xs flex items-center gap-1">
            <Camera className="w-3 h-3" /> {imgs.length}
          </div>
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {isNewProject && (
              <span
                data-testid={`new-project-badge-${p.id}`}
                className="bg-indigo-600 text-white text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded shadow-md flex items-center gap-1"
              >
                <Building2 className="w-3 h-3" /> Proyecto Nuevo
              </span>
            )}
            {p.featured && (
              <span className="bg-amber-500 text-white text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded shadow-sm">Destacado</span>
            )}
            {showVerified && (
              <span data-testid={`verified-badge-${p.id}`} className="bg-emerald-600 text-white text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded shadow-sm flex items-center gap-1">
                <BadgeCheck className="w-3 h-3" /> Verificada
              </span>
            )}
          </div>
        </Link>
        <div className="p-5 md:p-6 flex-1 flex flex-col justify-between">
          <div>
            <div className="flex items-baseline justify-between gap-3">
              <Link to={`/propiedad/${p.id}`} className="hover:text-habita-dark transition-colors">
                {dual ? (
                  <div className="flex flex-col" data-testid={`card-price-dual-${p.id}`}>
                    <span className="font-serif text-2xl font-bold text-habita leading-tight">{formatCOP(dualPrices.venta)} <span className="text-xs font-normal text-gray-500">venta</span></span>
                    <span className="text-base font-semibold text-habita-dark">{formatCOP(dualPrices.arriendo)} <span className="text-xs font-normal text-gray-500">/mes arriendo</span></span>
                  </div>
                ) : (
                  <h3 className="font-serif text-2xl font-bold text-habita">
                    {formatCOP(p.price)}
                    {p.operation === 'arriendo' && <span className="text-sm font-normal text-gray-500"> /mes</span>}
                  </h3>
                )}
              </Link>
              <span className="text-xs uppercase tracking-widest text-gray-500">{opLabel}</span>
            </div>
            <p className="mt-2 text-base font-medium text-gray-900 line-clamp-1">{p.title}</p>
            <p className="mt-1 text-sm text-gray-500 flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" /> {p.neighborhood ? `${p.neighborhood}, ` : ''}{p.city}
            </p>
            <p className="mt-3 text-sm text-gray-600 line-clamp-2">{p.description}</p>

            {advisorReason && (
              <AdvisorReasonBox reason={advisorReason} testId={`property-card-advisor-reason-${p.id}`} />
            )}
          </div>
          <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-5 text-sm text-gray-700">
              <span className="flex items-center gap-1.5"><Bed className="w-4 h-4 text-habita" /> {p.beds}</span>
              <span className="flex items-center gap-1.5"><Bath className="w-4 h-4 text-habita" /> {p.baths}</span>
              <span className="flex items-center gap-1.5"><Car className="w-4 h-4 text-habita" /> {p.parking}</span>
              <span className="flex items-center gap-1.5"><Maximize className="w-4 h-4 text-habita" /> {p.area} m²</span>
            </div>
            {agent && (
              <div className="hidden md:flex items-center gap-2 shrink-0">
                <img
                  src={agent.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(agent.name)}&background=00824D&color=fff`}
                  alt=""
                  className="w-7 h-7 rounded-full object-cover"
                />
                <span className="text-xs text-gray-600 truncate max-w-[120px]">{agent.name}</span>
                {showAgentVerified && <BadgeCheck className="w-3.5 h-3.5 text-habita shrink-0" />}
              </div>
            )}
          </div>
          <CardActionRow
            propertyId={p.id}
            waHref={waHref}
            onWhatsApp={handleWhatsAppClick}
            isNewProject={isNewProject}
            brochureUrl={p.brochure_url}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`property-card-${p.id}`}
      className="group bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg hover:border-habita/30 transition-all duration-300 flex flex-col"
    >
      <Link to={`/propiedad/${p.id}`} className="block relative aspect-[3/2] bg-gray-100 overflow-hidden">
        <img
          src={current.md}
          srcSet={!isPlaceholder ? `${current.sm} 480w, ${current.md} 720w, ${current.lg} 960w` : undefined}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          alt={p.title}
          width="720"
          height="540"
          className="w-full h-full object-cover gallery-img transition-transform duration-700 group-hover:scale-[1.05]"
          loading="lazy"
          decoding="async"
        />

        {/* Gradiente sutil bottom para asegurar legibilidad del contador de fotos */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/45 to-transparent pointer-events-none" />

        <div className="absolute top-3 right-3 flex flex-col gap-2 z-10">
          <button
            data-testid={`fav-btn-${p.id}`}
            onClick={handleFav}
            className="w-10 h-10 rounded-full bg-white/95 hover:bg-white flex items-center justify-center transition-all shadow-md hover:scale-105"
            aria-label="Favorito"
          >
            <Heart className={`w-4 h-4 transition-colors ${fav ? 'fill-rose-500 text-rose-500' : 'text-gray-700'}`} />
          </button>
          <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
            <ShareButton property={p} variant="icon" />
          </div>
        </div>
        <div className="absolute bottom-3 right-3 px-2.5 py-1 bg-black/60 backdrop-blur rounded-md text-white text-xs flex items-center gap-1 z-10">
          <Camera className="w-3 h-3" /> {imgs.length}
        </div>
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-10">
          {isNewProject && (
            <span
              data-testid={`new-project-badge-${p.id}`}
              className="bg-indigo-600 text-white text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded shadow-md flex items-center gap-1"
            >
              <Building2 className="w-3 h-3" /> Proyecto Nuevo
            </span>
          )}
          {p.featured && (
            <span className="bg-amber-500 text-white text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded shadow-sm">
              Destacado
            </span>
          )}
          {showVerified && (
            <span data-testid={`verified-badge-${p.id}`} className="bg-emerald-600 text-white text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded shadow-sm flex items-center gap-1">
              <BadgeCheck className="w-3 h-3" /> Verificada
            </span>
          )}
        </div>
        {/* Hover preview dots (desktop) */}
        <div className="absolute inset-0 hidden md:flex opacity-0 group-hover:opacity-100 transition-opacity">
          {imgs.slice(0, 4).map((_, i) => (
            <div
              key={i}
              onMouseEnter={(e) => { e.preventDefault(); setImgIdx(i); }}
              className="flex-1"
            />
          ))}
        </div>
      </Link>

      <div className="p-5 flex-1 flex flex-col">
        <Link to={`/propiedad/${p.id}`} className="block">
          <div className="flex items-baseline justify-between gap-2">
            {dual ? (
              <div className="flex flex-col" data-testid={`card-price-dual-compact-${p.id}`}>
                <span className="font-serif text-xl font-bold text-habita tracking-tight leading-tight">{formatCOP(dualPrices.venta)} <span className="text-[10px] font-normal text-gray-500">venta</span></span>
                <span className="text-sm font-semibold text-habita-dark">{formatCOP(dualPrices.arriendo)} <span className="text-[10px] font-normal text-gray-500">/mes</span></span>
              </div>
            ) : (
              <h3 className="font-serif text-2xl font-bold text-habita tracking-tight">
                {formatCOP(p.price)}
                {p.operation === 'arriendo' && <span className="text-xs font-normal text-gray-500 ml-1">/mes</span>}
              </h3>
            )}
            <span className="text-[10px] uppercase tracking-widest text-gray-500 shrink-0">{opLabel}</span>
          </div>
          <p className="mt-1.5 text-sm font-semibold text-gray-900 line-clamp-1">
            {isNewProject ? (p.constructor_project_name || p.title) : p.title}
          </p>
          {isNewProject && p.typology_name && (
            <p
              className="mt-0.5 text-xs text-indigo-700 font-semibold"
              data-testid={`new-project-typology-line-${p.id}`}
            >
              Tipología {p.typology_name} recomendada para ti
            </p>
          )}
          <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
            <MapPin className="w-3 h-3 shrink-0" />
            <span className="truncate">{p.neighborhood ? `${p.neighborhood}, ` : ''}{p.city}</span>
          </p>
        </Link>

        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-100 text-sm text-gray-700">
          <span className="flex items-center gap-1" title={`${p.beds} habitaciones`}><Bed className="w-4 h-4 text-habita" /> {p.beds}</span>
          <span className="flex items-center gap-1" title={`${p.baths} baños`}><Bath className="w-4 h-4 text-habita" /> {p.baths}</span>
          <span className="flex items-center gap-1" title={`${p.parking} parqueaderos`}><Car className="w-4 h-4 text-habita" /> {p.parking}</span>
          <span className="flex items-center gap-1 ml-auto text-xs text-gray-500">{p.area} m²</span>
        </div>

        {agent && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
            <img
              src={agent.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(agent.name)}&background=00824D&color=fff`}
              alt={agent.name}
              className="w-6 h-6 rounded-full object-cover"
              loading="lazy"
            />
            <span className="text-xs text-gray-600 truncate flex-1">{agent.name}</span>
            {showAgentVerified && (
              <span className="text-[10px] text-habita flex items-center gap-0.5 font-semibold">
                <BadgeCheck className="w-3.5 h-3.5" />
              </span>
            )}
          </div>
        )}

        {advisorReason && (
          <AdvisorReasonBox reason={advisorReason} testId={`property-card-advisor-reason-${p.id}`} />
        )}

        <div className="mt-auto pt-4">
          <CardActionRow
            propertyId={p.id}
            waHref={waHref}
            onWhatsApp={handleWhatsAppClick}
            isNewProject={isNewProject}
            brochureUrl={p.brochure_url}
          />
        </div>
      </div>
    </div>
  );
}


/* Caja del Advisor — pieza clave del rediseño: mint suave, borde
 * izquierdo verde grueso, tipografía cómoda y sin recortes agresivos.
 * Aparece SOLO cuando `advisorReason` está presente (regla del silencio). */
function AdvisorReasonBox({ reason, testId }) {
  return (
    <div
      data-testid={testId}
      className="mt-4 relative rounded-xl bg-habita-mint/50 border border-habita/15 pl-4 pr-4 py-3"
    >
      <div className="absolute left-0 top-2 bottom-2 w-1 rounded-full bg-habita" />
      <div className="flex items-center gap-1.5 mb-1">
        <Sparkles className="w-3.5 h-3.5 text-habita" />
        <span className="text-[10px] uppercase tracking-widest font-bold text-habita">
          Habita Advisor
        </span>
      </div>
      <p className="text-sm text-habita-dark leading-relaxed">
        {reason}
      </p>
    </div>
  );
}


/* Fila de acciones directas debajo de cada card:
 * - Ver ficha (primary) → abre PropertyDetail.
 * - WhatsApp (secondary verde) → deep-link wa.me con mensaje pre-armado.
 * Si el agente no tiene teléfono, solo se muestra "Ver ficha".
 */
function CardActionRow({ propertyId, waHref, onWhatsApp, isNewProject = false, brochureUrl = null }) {
  // Para vivienda nueva: el CTA principal es "Ver Brochure".
  // WhatsApp queda como CTA secundario (misma prioridad visual que en cards regulares).
  const primary = isNewProject && brochureUrl ? (
    <a
      href={brochureUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`brochure-btn-${propertyId}`}
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center justify-center gap-1.5 h-10 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors"
    >
      <FileText className="w-4 h-4" /> Ver Brochure
    </a>
  ) : (
    <Link
      to={`/propiedad/${propertyId}`}
      data-testid={`view-btn-${propertyId}`}
      className="inline-flex items-center justify-center gap-1.5 h-10 rounded-full bg-habita text-white text-sm font-semibold hover:opacity-90 transition-opacity"
    >
      Ver ficha <ArrowUpRight className="w-4 h-4" />
    </Link>
  );

  return (
    <div className="grid grid-cols-2 gap-2" data-testid={`card-actions-${propertyId}`}>
      {primary}
      {waHref ? (
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onWhatsApp}
          data-testid={`card-whatsapp-btn-${propertyId}`}
          className="inline-flex items-center justify-center gap-1.5 h-10 rounded-full border-2 border-emerald-600 text-emerald-700 text-sm font-semibold hover:bg-emerald-50 transition-colors"
        >
          <WhatsAppIcon className="w-4 h-4" /> WhatsApp
        </a>
      ) : isNewProject ? (
        // Vivienda nueva sin agente cargado: no forzamos placeholder — dejamos hueco cerrado.
        <span
          className="inline-flex items-center justify-center gap-1.5 h-10 rounded-full border-2 border-gray-200 text-gray-400 text-xs italic cursor-default"
          data-testid={`card-whatsapp-unavailable-${propertyId}`}
        >
          Contactar constructora
        </span>
      ) : (
        <span
          className="inline-flex items-center justify-center gap-1.5 h-10 rounded-full border-2 border-gray-200 text-gray-400 text-xs italic cursor-default"
          data-testid={`card-whatsapp-unavailable-${propertyId}`}
        >
          Sin WhatsApp
        </span>
      )}
    </div>
  );
}
